import BaseAIProvider from '../core/BaseAIProvider';
import { CV_EXTRACTION_PROMPT, IMMIGRATION_ANALYSIS_PROMPT } from '../config/promptTemplates';

/**
 * Google Gemini AI Provider
 * Adapter for Gemini 2.5 Flash and other Gemini models
 */
class GeminiProvider extends BaseAIProvider {
  constructor(config = {}) {
    super(config);
    this.baseUrl = config.baseUrl || `https://generativelanguage.googleapis.com/v1beta/models/${this.model}:generateContent`;
  }

  getProviderName() {
    return 'gemini';
  }

  getPricing() {
    return {
      inputCostPerMillion: 0.075,
      outputCostPerMillion: 0.30,
      currency: 'USD'
    };
  }

  getRateLimits() {
    return {
      requestsPerMinute: 15,
      tokensPerMinute: 4000000,
      requestsPerDay: 1500
    };
  }

  /**
   * Call Gemini API
   */
  async callGemini(requestBody, purpose = 'analysis') {
    this.validateConfig();

    try {
      const response = await fetch(`${this.baseUrl}?key=${this.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[Gemini Provider] (${purpose}) API error:`, errorText);
        throw new Error(`Gemini API error (${response.status}): ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`[Gemini Provider] (${purpose}) Request failed:`, error);
      throw this.handleError(error);
    }
  }

  /**
   * Extract text from Gemini response
   */
  extractCandidateText(response) {
    try {
      const candidate = response?.candidates?.[0];
      if (!candidate) return '';

      const serializePart = (part) => {
        if (!part) return '';
        if (typeof part.text === 'string') return part.text;

        if (typeof part.inlineData?.data === 'string') {
          try {
            if (typeof atob === 'function') {
              return atob(part.inlineData.data);
            }
            if (typeof Buffer !== 'undefined') {
              return Buffer.from(part.inlineData.data, 'base64').toString('utf-8');
            }
          } catch (_) {
            // Fall through to raw data
          }
          return part.inlineData.data;
        }

        if (part.functionCall) {
          const fnArgs = this.extractFunctionCallArgs(part.functionCall);
          if (fnArgs) return fnArgs;
          return JSON.stringify(part.functionCall);
        }

        if (part.json != null) {
          return typeof part.json === 'string' ? part.json : JSON.stringify(part.json);
        }

        if (part.protectedContent && typeof part.protectedContent === 'string') {
          return part.protectedContent;
        }

        if (part.code) {
          const { code: { language, code } = {} } = part;
          return code ? `${language || 'text'}:${code}` : '';
        }

        if (part.structValue) {
          return JSON.stringify(part.structValue);
        }

        return '';
      };

      if (Array.isArray(candidate.content?.parts)) {
        const text = candidate.content.parts
          .map((part) => serializePart(part))
          .filter(Boolean)
          .join('')
          .trim();
        if (text.length > 0) return text;
      }

      if (typeof candidate.output === 'string') {
        return candidate.output.trim();
      }

      if (candidate?.functionCall) {
        const fnArgs = this.extractFunctionCallArgs(candidate.functionCall);
        if (fnArgs) return fnArgs;
        return JSON.stringify(candidate.functionCall);
      }

      return '';
    } catch (error) {
      console.error('[Gemini Provider] Failed to extract candidate text:', error);
      return '';
    }
  }

  /**
   * Extract function call arguments
   */
  extractFunctionCallArgs(fn) {
    if (!fn || typeof fn !== 'object') return null;

    const directArgs = fn.arguments ?? fn.args ?? fn.argumentsJson ?? fn.parameters;
    if (typeof directArgs === 'string' && directArgs.trim().length > 0) {
      return directArgs;
    }

    if (directArgs && typeof directArgs === 'object') {
      try {
        return JSON.stringify(directArgs);
      } catch (_) {
        return null;
      }
    }

    if (Array.isArray(fn.parameters?.arguments)) {
      try {
        return JSON.stringify(fn.parameters.arguments);
      } catch (_) {
        return null;
      }
    }

    return null;
  }

  /**
   * Safe JSON parsing with multiple fallbacks
   */
  safeParseJSON(value) {
    if (typeof value !== 'string') return null;
    let trimmed = value.trim();
    if (!trimmed) return null;

    try {
      trimmed = trimmed.replace(/```json\s*/g, '').replace(/```\s*/g, '');
      trimmed = trimmed.replace(/[\u0000-\u001F\u007F]/g, ' ');
      trimmed = trimmed.replace(/,(\s*[}\]])/g, '$1');
      return JSON.parse(trimmed);
    } catch (error) {
      const startIdx = trimmed.indexOf('{');
      const endIdx = trimmed.lastIndexOf('}');
      if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
        const candidate = trimmed.slice(startIdx, endIdx + 1);
        try {
          return JSON.parse(candidate);
        } catch (_) {
          return null;
        }
      }
      return null;
    }
  }

  /**
   * Extract structured object from response parts
   */
  extractStructuredObject(parts) {
    if (!Array.isArray(parts)) return null;

    let combinedText = '';

    for (const part of parts) {
      if (!part) continue;

      if (typeof part.text === 'string') {
        combinedText += part.text;
        const parsed = this.safeParseJSON(part.text);
        if (parsed) return parsed;
      }

      const fnArgs = this.extractFunctionCallArgs(part.functionCall);
      if (fnArgs) {
        const parsedFn = this.safeParseJSON(fnArgs);
        if (parsedFn) return parsedFn;
      }

      if (part.json != null) {
        if (typeof part.json === 'string') {
          const parsed = this.safeParseJSON(part.json);
          if (parsed) return parsed;
        } else if (typeof part.json === 'object') {
          return part.json;
        }
      }

      if (part.structValue) {
        return this.convertStructToObject(part.structValue);
      }
    }

    if (combinedText.trim().length > 0) {
      const parsedCombined = this.safeParseJSON(combinedText);
      if (parsedCombined) return parsedCombined;
    }

    return null;
  }

  /**
   * Convert Gemini struct format to plain object
   */
  convertStructToObject(struct) {
    if (!struct || typeof struct !== 'object') return {};
    const fields = struct.fields || {};
    const result = {};
    for (const [key, value] of Object.entries(fields)) {
      result[key] = this.convertStructValue(value);
    }
    return result;
  }

  convertStructValue(value) {
    if (value == null) return null;

    if (Array.isArray(value)) {
      return value.map((item) => this.convertStructValue(item));
    }

    if (typeof value === 'object' && value !== null) {
      if (value.structValue) {
        return this.convertStructToObject(value.structValue);
      }
      if (value.listValue) {
        const list = Array.isArray(value.listValue?.values)
          ? value.listValue.values
          : Array.isArray(value.listValue)
            ? value.listValue
            : [];
        return list.map((item) => this.convertStructValue(item));
      }
      if (value.stringValue !== undefined) return value.stringValue;
      if (value.numberValue !== undefined) return Number(value.numberValue);
      if (value.boolValue !== undefined) return Boolean(value.boolValue);
      if (value.nullValue !== undefined) return null;

      const result = {};
      for (const [key, nested] of Object.entries(value)) {
        result[key] = this.convertStructValue(nested);
      }
      return result;
    }

    return value;
  }

  /**
   * Analyze CV - Implementation
   */
  async analyzeCV(cvText, options = {}) {
    try {
      console.log('[Gemini Provider] Starting CV extraction...');

      const prompt = CV_EXTRACTION_PROMPT(cvText);
      const buildRequestBody = (maxTokens) => ({
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: options.temperature ?? 0.1,
          topK: options.topK ?? 40,
          topP: options.topP ?? 0.95,
          maxOutputTokens: maxTokens
        },
        safetySettings: [
          { category: "HARM_CATEGORY_HARASSMENT", threshold: "BLOCK_MEDIUM_AND_ABOVE" },
          { category: "HARM_CATEGORY_HATE_SPEECH", threshold: "BLOCK_MEDIUM_AND_ABOVE" },
          { category: "HARM_CATEGORY_SEXUALLY_EXPLICIT", threshold: "BLOCK_MEDIUM_AND_ABOVE" },
          { category: "HARM_CATEGORY_DANGEROUS_CONTENT", threshold: "BLOCK_MEDIUM_AND_ABOVE" }
        ]
      });

      let maxTokens = options.maxTokens || 2048;
      let attempts = 0;
      let data = null;

      // Retry loop for token limits
      while (attempts < 3) {
        const requestBody = buildRequestBody(maxTokens);
        data = await this.callGemini(requestBody, 'cv-extraction');

        const finishReason = data?.candidates?.[0]?.finishReason;
        console.log('[Gemini Provider] Finish reason:', finishReason, 'tokens:', maxTokens);

        if (finishReason !== 'MAX_TOKENS' || maxTokens >= 6144) break;

        maxTokens = Math.min(maxTokens + 2048, 6144);
        attempts += 1;
        console.warn('[Gemini Provider] Retrying with maxTokens =', maxTokens);
      }

      if (!data) {
        throw new Error('Gemini returned no data');
      }

      const candidate = data?.candidates?.[0];
      const parts = candidate?.content?.parts || [];
      const analysisText = this.extractCandidateText(data);
      let structuredObject = this.extractStructuredObject(parts);

      if (!structuredObject) {
        const candidateFnArgs = this.extractFunctionCallArgs(candidate?.functionCall);
        if (candidateFnArgs) {
          structuredObject = this.safeParseJSON(candidateFnArgs) || structuredObject;
        }
      }

      const parseSource = analysisText && analysisText.trim().length > 0 ? analysisText : structuredObject;

      if (!parseSource) {
        console.error('[Gemini Provider] No structured CV text in response');
        return this.buildFallbackCVData(cvText);
      }

      let result = this.parseCVResponse(parseSource);

      if (result?.error && structuredObject && parseSource !== structuredObject) {
        const structuredResult = this.parseCVResponse(structuredObject);
        if (!structuredResult.error) {
          result = structuredResult;
        }
      }

      return result;
    } catch (error) {
      console.error('[Gemini Provider] CV analysis error:', error);
      return this.buildFallbackCVData(cvText, error.message);
    }
  }

  /**
   * Parse CV response into normalized format
   */
  parseCVResponse(responseInput) {
    try {
      let parsed = null;

      if (typeof responseInput === 'string') {
        const cleanedInput = responseInput.trim();
        const jsonMatch = cleanedInput.match(/```json\s*([\s\S]*?)```/) ||
          cleanedInput.match(/```\s*([\s\S]*?)```/) ||
          [null, cleanedInput];

        const jsonStr = jsonMatch[1] || cleanedInput;
        parsed = JSON.parse(jsonStr);

        if (parsed && parsed.fields && !parsed.personalInfo) {
          parsed = this.convertStructToObject(parsed);
        }
      } else if (responseInput && typeof responseInput === 'object') {
        if (responseInput.fields) {
          parsed = this.convertStructToObject(responseInput);
        } else {
          parsed = responseInput;
        }
      }

      if (!parsed || typeof parsed !== 'object') {
        throw new Error('Unable to parse CV response');
      }

      return {
        personalInfo: parsed.personalInfo || {},
        education: Array.isArray(parsed.education) ? parsed.education : [],
        experience: Array.isArray(parsed.experience) ? parsed.experience : [],
        skills: parsed.skills || { technical: [], programming: [], tools: [], certifications: [] },
        summary: parsed.summary || '',
        strengths: Array.isArray(parsed.strengths) ? parsed.strengths : [],
        languageProficiency: parsed.languageProficiency || { english: 'unknown', other: [] },
        assessment: parsed.assessment || { score: 5, reasoning: 'Unable to assess' },
        totalExperienceYears: parsed.totalExperienceYears || '',
        rawText: parsed.rawText || '',
        provider: this.getProviderName(),
        model: this.getModelName()
      };
    } catch (error) {
      console.error('[Gemini Provider] Error parsing CV response:', error);
      return this.buildFallbackCVData(typeof responseInput === 'string' ? responseInput : '', error.message);
    }
  }

  /**
   * Build fallback CV data structure
   */
  buildFallbackCVData(rawText = '', errorMessage = '') {
    return {
      personalInfo: {},
      education: [],
      experience: [],
      skills: { technical: [], programming: [], tools: [], certifications: [] },
      summary: 'Analysis unavailable',
      strengths: [],
      languageProficiency: { english: 'unknown', other: [] },
      assessment: { score: 5, reasoning: 'Unable to parse response' },
      totalExperienceYears: '',
      rawText,
      error: errorMessage || 'Failed to analyze CV',
      provider: this.getProviderName(),
      model: this.getModelName()
    };
  }

  /**
   * Analyze Immigration Profile - Implementation
   */
  async analyzeImmigrationProfile(userProfile, extractedData, cvText, options = {}) {
    try {
      console.log('[Gemini Provider] Starting immigration analysis...');

      const prompt = IMMIGRATION_ANALYSIS_PROMPT(userProfile, extractedData, cvText);
      const buildRequestBody = (maxTokens) => ({
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: options.temperature ?? 0.2,
          topK: options.topK ?? 32,
          topP: options.topP ?? 0.9,
          maxOutputTokens: maxTokens
        },
        safetySettings: [
          { category: "HARM_CATEGORY_HARASSMENT", threshold: "BLOCK_MEDIUM_AND_ABOVE" },
          { category: "HARM_CATEGORY_HATE_SPEECH", threshold: "BLOCK_MEDIUM_AND_ABOVE" },
          { category: "HARM_CATEGORY_SEXUALLY_EXPLICIT", threshold: "BLOCK_MEDIUM_AND_ABOVE" },
          { category: "HARM_CATEGORY_DANGEROUS_CONTENT", threshold: "BLOCK_MEDIUM_AND_ABOVE" }
        ]
      });

      let maxTokens = options.maxTokens || 4096;
      let attempts = 0;
      let data = null;

      while (attempts < 3) {
        const requestBody = buildRequestBody(maxTokens);
        data = await this.callGemini(requestBody, 'immigration-analysis');

        const finishReason = data?.candidates?.[0]?.finishReason;
        console.log('[Gemini Provider] Immigration finish reason:', finishReason);

        if (finishReason !== 'MAX_TOKENS' || maxTokens >= 8192) break;

        maxTokens = Math.min(maxTokens + 2048, 8192);
        attempts += 1;
      }

      if (!data) {
        throw new Error('Gemini returned no data');
      }

      const candidate = data?.candidates?.[0];
      const parts = candidate?.content?.parts || [];
      const responseText = this.extractCandidateText(data);
      let structuredObject = this.extractStructuredObject(parts);

      if (!structuredObject) {
        const candidateFnArgs = this.extractFunctionCallArgs(candidate?.functionCall);
        if (candidateFnArgs) {
          structuredObject = this.safeParseJSON(candidateFnArgs) || structuredObject;
        }
      }

      const parseSource = responseText && responseText.trim().length > 0 ? responseText : structuredObject;

      if (!parseSource) {
        console.error('[Gemini Provider] No immigration analysis received');
        return this.buildFallbackImmigrationAnalysis(userProfile, cvText);
      }

      const fallback = this.buildFallbackImmigrationAnalysis(userProfile, cvText);
      let parsed = this.parseImmigrationResponse(parseSource, fallback);

      if (parsed?.error && structuredObject && parseSource !== structuredObject) {
        const structuredParsed = this.parseImmigrationResponse(structuredObject, fallback);
        if (!structuredParsed.error) {
          parsed = structuredParsed;
        }
      }

      return {
        ...parsed,
        provider: this.getProviderName(),
        model: this.getModelName()
      };
    } catch (error) {
      console.error('[Gemini Provider] Immigration analysis error:', error);
      const fallback = this.buildFallbackImmigrationAnalysis(userProfile, cvText);
      fallback.error = error.message;
      fallback.provider = this.getProviderName();
      fallback.model = this.getModelName();
      return fallback;
    }
  }

  /**
   * Parse immigration analysis response
   */
  parseImmigrationResponse(responseInput, fallback) {
    try {
      let parsed = null;

      if (typeof responseInput === 'string') {
        let cleaned = responseInput.trim();
        cleaned = cleaned.replace(/```json\s*/g, '').replace(/```\s*/g, '');
        cleaned = cleaned.replace(/[\u0000-\u001F\u007F]/g, ' ');
        cleaned = cleaned.replace(/,(\s*[}\]])/g, '$1');
        parsed = JSON.parse(cleaned);

        if (parsed && parsed.fields) {
          parsed = this.convertStructToObject(parsed);
        }
      } else if (responseInput && typeof responseInput === 'object') {
        if (responseInput.fields) {
          parsed = this.convertStructToObject(responseInput);
        } else {
          parsed = responseInput;
        }
      }

      if (!parsed || typeof parsed !== 'object') {
        throw new Error('Unable to parse immigration analysis');
      }

      // Deep merge with fallback
      const merged = this.deepMerge(fallback, parsed);
      if (!Array.isArray(merged.recommendedPathways)) {
        merged.recommendedPathways = [];
      }

      return merged;
    } catch (error) {
      console.error('[Gemini Provider] Failed to parse immigration analysis:', error);
      return {
        ...fallback,
        error: 'Failed to parse immigration analysis'
      };
    }
  }

  /**
   * Deep merge objects
   */
  deepMerge(base = {}, override = {}) {
    const result = { ...base };
    Object.entries(override).forEach(([key, value]) => {
      if (value == null) return;

      if (Array.isArray(value)) {
        result[key] = value;
        return;
      }

      if (typeof value === 'object' && !Array.isArray(value)) {
        const baseChild = typeof base[key] === 'object' && base[key] !== null ? base[key] : {};
        result[key] = this.deepMerge(baseChild, value);
        return;
      }

      result[key] = value;
    });
    return result;
  }

  /**
   * Build fallback immigration analysis
   */
  buildFallbackImmigrationAnalysis(userProfile, cvText) {
    return {
      educationAssessment: {
        level: userProfile?.education?.highestEducation || "To be determined",
        institutions: [],
        fields: [],
        recognition: "Pending detailed evaluation",
        relevance: "Requires consultant review",
        institutionQuality: "Unknown",
        gpaStrength: "Not specified",
        additionalEducation: []
      },
      experienceAnalysis: {
        positions: [],
        totalYears: userProfile?.experience?.totalExperience || "0",
        relevantExperience: "0",
        careerProgression: "Standard profile",
        industryDemand: "Moderate",
        seniority: "Entry to Mid-level",
        companyReputation: "Standard",
        employmentGaps: "Not identified",
        currentSalary: "Not disclosed",
        leadershipExperience: "To be verified"
      },
      skillsEvaluation: {
        technicalSkills: {
          programming: [],
          frameworks: [],
          software: [],
          specializations: [],
          inDemandSkills: []
        },
        languageSkills: {
          english: userProfile?.languageSkills?.englishProficiency || "Unknown",
          german: "Not specified",
          otherLanguages: [],
          languageAdvantage: "Language test recommended"
        },
        softSkills: [],
        certifications: [],
        professionalMemberships: []
      },
      projectsAndAchievements: {
        notableProjects: [],
        awards: [],
        publications: [],
        speakingEngagements: []
      },
      immigrationStrengths: [
        "Profile under review",
        "Detailed eligibility assessment pending"
      ],
      potentialConcerns: [
        "Complete documentation review required",
        "Language proficiency verification needed"
      ],
      recommendedPathways: [],
      selectedPathwayAnalysis: {
        pathway: userProfile?.reviewData?.selectedPathway || "To be determined",
        country: userProfile?.countrySelection?.selectedCountry || "Germany",
        eligibilityScore: 50,
        scoreBreakdown: {
          education: 10,
          experience: 10,
          language: 10,
          financialReadiness: 10,
          ageOptimality: 10
        },
        eligible: "Fair",
        strengths: ["Standard profile"],
        weaknesses: ["Requires detailed assessment"],
        missingRequirements: ["Complete documentation"],
        timelineEstimate: "6-12 months",
        successProbability: 50,
        improvementActions: [
          "Submit complete documentation",
          "Schedule language proficiency test"
        ]
      },
      alternativePathways: [],
      salaryExpectations: {
        currentSalary: "Not specified",
        targetCountrySalary: "Market dependent",
        competitiveness: "Standard"
      },
      missingCriticalInfo: [
        "Detailed work experience",
        "Language test scores",
        "Educational credential evaluation"
      ],
      recommendedNextSteps: [
        "Schedule detailed consultation with immigration specialist",
        "Gather academic and employment documentation",
        "Prepare for language proficiency assessment"
      ],
      documentChecklist: [
        "Passport bio page",
        "Academic transcripts and certificates",
        "Employment letters or contracts",
        "Financial statements / proof of funds"
      ],
      visaReadiness: {
        status: "Pending assessment",
        reasoning: "Awaiting detailed documentation review and language verification",
        priorityActions: [
          "Collect academic certificates",
          "Prepare financial readiness proof",
          "Begin language test preparation"
        ]
      },
      overallAssessment: cvText
        ? "Analysis partially completed. Profile requires consultant review for full immigration assessment."
        : "Complete profile assessment pending. Please provide CV text or structured data."
    };
  }
}

export default GeminiProvider;
